import { zodResolver } from '@hookform/resolvers/zod';
import { CostUnitType, LineItemType, SurchargeUnit } from '@pelicargo/types';
import { useEffect } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

import { getDifferences } from '../../utils/findChangedKeys';
import { QuoteLineItem } from '../../utils/types';

type UseLineItemForm = { methods: UseFormReturn<QuoteLineItem> };
type Options = { isCreate: boolean };

const defaultOptions = { isCreate: false };

export const validationSchema = z.object({
  label: z.string(),
  type: z.nativeEnum(LineItemType).optional(),
  is_per_unit: z.coerce.number().nullish(),
  per_unit_cost: z.coerce
    .number({ invalid_type_error: 'Rate must be a number' })
    .refine((val) => /^-?\d*\.?\d{0,2}$/.test(val.toString()), {
      message: 'Rate must be a valid dollar amount or percentage',
    }),
  per_unit_cost_type: z.nativeEnum(CostUnitType),
  per_unit_count: z.coerce.number(),
  per_unit_count_type: z.nativeEnum(SurchargeUnit),
  cost: z.coerce
    .number({ invalid_type_error: 'Cost must be a number' })
    .refine((val) => /^-?\d*\.?\d{0,2}$/.test(val.toString()), {
      message: 'Cost must be a valid dollar amount',
    }),
});

export const useLineItemForm = (data: QuoteLineItem, { isCreate }: Options = defaultOptions): UseLineItemForm => {
  const methods = useForm<QuoteLineItem>({
    mode: 'onBlur',
    resolver: zodResolver(validationSchema),
    defaultValues: data,
  });
  const { getValues, setValue } = methods;

  useEffect(() => {
    const updateFields = () => {
      const changes = getDifferences(getValues(), data);
      if (!changes.length) return;

      for (const [key, value] of changes) {
        if (value !== undefined) setValue<any>(key, value);
      }
    };

    // Run if we're not on create & we have data
    if (!isCreate && data) updateFields();
  }, [data, getValues, setValue, isCreate]);

  return { methods: { ...methods, getValues, setValue } };
};
